import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./TabsSection.css";
import { Check } from "lucide-react";
import {
  bankingSolutionTabs,
  enterpriseAiTabs,
  dataAnalyticsTabs,
  cyberSecurityTabs,
  solutionsServicesTabs,
  cloudServicesTabs,
} from "../../StaticData/TabsData";

const OfferingsTabs = () => {
  const [activeTab, setActiveTab] = useState(1);
  const [tabsData, setTabsData] = useState(bankingSolutionTabs);
  const sectionRef = useRef(null);
  const [selectedService] = useState(window.location.pathname.split("/")[1]);

  useEffect(() => {
    switch (selectedService) {
      case "banking-solutions":
        setTabsData(bankingSolutionTabs);
        break;
      case "enterprise-ai":
        setTabsData(enterpriseAiTabs);
        break;
      case "data-analytics":
        setTabsData(dataAnalyticsTabs);
        break;
      case "next-gen-modernization":
        setTabsData(solutionsServicesTabs);
        break;
      case "managed-cybersecurity":
        setTabsData(cyberSecurityTabs);
        break;
      case "cloud-innovations":
        setTabsData(cloudServicesTabs);
        break;
      default:
        setTabsData(bankingSolutionTabs);
    }
    setActiveTab(1); // Reset to the first tab when service changes
  }, [selectedService]);

  return (
    <section ref={sectionRef} className="tabs-section">
      <div className="section-heading">
        <motion.div
          className="Productright"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true, amount: 0.3 }}
        >
          <h1 className="section-title">Our Offerings</h1>
        </motion.div>
      </div>
      <br />
      {/* Tabs Navigation */}
      <div className="tabs-container">
        {tabsData.map((tab) => (
          <button
            key={tab.id}
            className={`tab-button ${tab.id === activeTab ? "active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.tabTitle}
          </button>
        ))}
      </div>

      {/* Content & Image with Smooth Animations */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 1, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
          className="tab-content-container"
          style={{
            background: tabsData[activeTab - 1]?.useGradient
              ? `radial-gradient(ellipse 50% 110% at 77% 50%, 
                  ${tabsData[activeTab - 1]?.bgColor} -100%, 
                  rgba(${tabsData[activeTab - 1]?.gradientColor}, 100) 5%, 
                  black 50%)`
              : tabsData[activeTab - 1]?.bgColor,
          }}
        >
          <div className="tab-content">
            <h1 style={{ color: tabsData[activeTab - 1].headingColor }}>
              {tabsData[activeTab - 1].heading}
            </h1>
            <p style={{ color: tabsData[activeTab - 1].headingColor }}>
              {tabsData[activeTab - 1].content}
            </p>
            <ul className="features-list">
              {tabsData[activeTab - 1].features.map((feature, index) => (
                <li
                  key={index}
                  className="feature-item"
                  style={{ color: tabsData[activeTab - 1].headingColor }}
                >
                  <Check size={18} color="white" /> {feature}
                </li>
              ))}
            </ul>
            {tabsData[activeTab - 1].button && (
              <a href={tabsData[activeTab - 1].button.href}>
                <button
                  className="mt-4 transition-all hover:scale-105"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "white",
                    color: "#000000",
                    border: "2px solid white",
                    fontSize: "16px",
                    fontWeight: "600",
                    padding: "4px 14px",
                    borderRadius: "14px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                    lineHeight: "normal",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = "#f0f0f0";
                    e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.25)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = "white";
                    e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.15)";
                  }}
                >
                  <span style={{ display: "block", marginTop: "4px" }}>
                    {tabsData[activeTab - 1].button.label}
                  </span>
                </button>
              </a>
            )}
            <p style={{ color: tabsData[activeTab - 1].headingColor, paddingTop: "10px" }}>
              {tabsData[activeTab - 1].inferiq}
            </p>
          </div>
          <motion.img
            src={tabsData[activeTab - 1].img}
            alt="Tab Image"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            className="tab-image"
            style={{
              padding: tabsData[activeTab - 1].padding,
              maxWidth: tabsData[activeTab - 1].maxWidth,
              maxHeight: tabsData[activeTab - 1].maxHeight,
              objectFit: tabsData[activeTab - 1].objectFit,
              flex: tabsData[activeTab - 1].flex,
              width: tabsData[activeTab - 1].width,
            }}
          />
        </motion.div>
      </AnimatePresence>
    </section>
  );
};

export default OfferingsTabs;
